// frontend/src/app/services/web-push.service.ts
import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  doc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
  getDoc,
} from '@angular/fire/firestore';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  Messaging,
} from 'firebase/messaging';

@Injectable({ providedIn: 'root' })
export class WebPushService {
  private db = inject(Firestore);
  private auth = inject(Auth);
  private injector = inject(Injector);

  private messaging: Messaging | null = null;

  // ✅ init no debe bloquear rehidratación tras hard reset
  private initialized = false;

  // throttle para "seen" (best-effort)
  private lastSeenWriteMs = 0;

  // ✅ cache para poder rehidratar tokens si hardReset borra subcolección
  private lastUid: string | null = null;
  private lastToken: string | null = null;
  private lastPlatform: 'web-pwa' | 'web-hosting' | null = null;

  // ✅ guard timer para rehidratar tokens si desaparecen
  private tokenGuardTimer: any = null;

  /**
   * ⚠️ IMPORTANTE:
   * Public key (VAPID) de:
   * Firebase → Project settings → Cloud Messaging → Web Push certificates → Public key
   */
  private readonly VAPID_PUBLIC_KEY_RAW =
    'BHjc5WgCSYlCelYL-PYTfJg6gKSUB-LRglFv1rv-dmLNopHHdvt6wBQhfckHtlspYSWyHLNMyT1hYZMJ2iHspeA';

  /**
   * ✅ Scope aislado para el SW de Firebase Messaging.
   * Esto evita conflictos con Angular Service Worker (ngsw-worker.js),
   * que es el que debe controlar la app entera.
   */
  private readonly FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

  /**
   * Inicializa (y/o rehidrata) Web Push.
   * ✅ Si ya estaba inicializado, hace "ensure" para recuperar tokens tras hard reset.
   */
  async init() {
    try {
      // ✅ Soporte
      const supported = await isSupported();
      if (!supported) {
        console.warn(
          '[WebPush] FCM Web no soportado en este navegador (ej: Safari iOS).'
        );
        return;
      }

      if (!('Notification' in window)) {
        console.warn('[WebPush] Notification API missing');
        return;
      }

      if (!window.isSecureContext) {
        console.warn(
          '[WebPush] Not secure context. Push sólo funciona en https:// o en http://localhost.'
        );
        return;
      }

      const vapidKey = this.VAPID_PUBLIC_KEY_RAW.trim();
      if (!vapidKey || vapidKey.length < 80) {
        console.error('[WebPush] VAPID_PUBLIC_KEY parece inválida.');
        return;
      }

      const uid = await this.waitForUser();
      if (!uid) {
        console.warn('[WebPush] No user logueado todavía');
        return;
      }
      this.lastUid = uid;

      // ✅ Permiso (NO se volverá a pedir si ya está granted/denied)
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();

      if (perm !== 'granted') {
        console.warn('[WebPush] Permiso de notificación no concedido');
        return;
      }

      // ✅ Asegura SW
      const swReg = await this.ensureServiceWorker();

      // ✅ Messaging singleton
      if (!this.messaging) this.messaging = getMessaging();

      // ✅ Obtener token (puede rotar con el tiempo)
      const token = await getToken(this.messaging, {
        vapidKey,
        serviceWorkerRegistration: swReg,
      });

      if (!token) {
        console.warn('[WebPush] token vacío/null');
        return;
      }

      // ✅ Plataforma (PWA vs browser)
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;

      const platform = (isStandalone ? 'web-pwa' : 'web-hosting') as
        | 'web-pwa'
        | 'web-hosting';

      this.lastToken = token;
      this.lastPlatform = platform;

      // ✅ Si es primera vez real, desactivar tokens viejos del mismo platform (best-effort)
      // (Si ya estaba inicializado también lo permitimos: no rompe nada)
      await this.disableOldWebTokens(uid, platform, token);

      // ✅ Guardar token SIEMPRE (merge true) → esto rehidrata tras hard reset
      await this.upsertWebToken(uid, platform, token);
      console.log('[WebPush] token guardado/rehidratado. platform=', platform, 'type=web');

      // ✅ Best-effort: marcar seen al registrar (útil para nudges)
      await this.markUserSeen(uid, 'webPushInit');

      // ✅ Foreground push
      onMessage(this.messaging, (payload) => {
        console.log('[WebPush] foreground push recibido:', payload);
        this.markUserSeen(uid, 'webForegroundPush').catch(() => {});
      });

      // ✅ Arranca guard: si hardReset borra users/{uid}/pushTokens/{token}, lo re-creamos
      this.startTokenGuard();

      this.initialized = true;
    } catch (e) {
      console.error('[WebPush] init error', e);
    }
  }

  /**
   * ✅ Upsert token web en Firestore (idempotente).
   * Si el doc existe, lo actualiza; si fue borrado por reset, lo recrea.
   */
  private async upsertWebToken(uid: string, platform: string, token: string) {
    await setDoc(
      doc(this.db, `users/${uid}/pushTokens/${token}`),
      {
        token,

        // ✅ CLAVE: backend lo separa por web/native
        type: 'web',

        platform, // 'web-pwa' | 'web-hosting'
        origin: window.location.origin,
        displayMode: platform === 'web-pwa' ? 'standalone' : 'browser',
        ua: navigator.userAgent || null,

        enabled: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  /**
   * Asegura que el Service Worker de Firebase Messaging esté registrado.
   *
   * ✅ IMPORTANTÍSIMO:
   * NO usar scope "/" aquí, porque compite con Angular Service Worker (ngsw-worker.js)
   */
  private async ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
    console.log(
      `[WebPush] registrando ServiceWorker /firebase-messaging-sw.js (scope=${this.FCM_SW_SCOPE})`
    );

    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: this.FCM_SW_SCOPE,
    });

    await navigator.serviceWorker.ready.catch(() => {});
    return reg;
  }

  /**
   * Espera a que haya un usuario autenticado y devuelve su uid
   */
  private waitForUser(): Promise<string | null> {
    const u = this.auth.currentUser;
    if (u?.uid) return Promise.resolve(u.uid);

    return new Promise((resolve) => {
      const off = onAuthStateChanged(this.auth, (usr) => {
        off();
        resolve(usr?.uid ?? null);
      });
    });
  }

  /**
   * ✅ Desactiva tokens WEB anteriores del mismo platform (web-pwa / web-hosting),
   * dejando activo el token actual.
   *
   * Nota: best-effort. Si no hay índices, no rompe nada.
   */
  private async disableOldWebTokens(uid: string, platform: string, currentToken: string) {
    try {
      const colRef = collection(this.db, `users/${uid}/pushTokens`);
      const q = query(colRef, where('type', '==', 'web'), where('platform', '==', platform));
      const snap = await getDocs(q);

      if (snap.empty) return;

      await Promise.all(
        snap.docs
          .filter((d) => d.id !== currentToken)
          .map((d) =>
            setDoc(
              doc(this.db, d.ref.path),
              {
                enabled: false,
                disabledAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            )
          )
      );
    } catch (e) {
      console.warn('[WebPush] disableOldWebTokens failed (best-effort):', e);
    }
  }

  /**
   * ✅ Guardia: cada 2 min comprueba si el token doc existe.
   * Si hardReset lo borró, lo re-crea sin pedir permisos.
   */
  private startTokenGuard() {
    if (this.tokenGuardTimer) return;

    this.tokenGuardTimer = setInterval(async () => {
      try {
        const uid = this.lastUid;
        const token = this.lastToken;
        const platform = this.lastPlatform;

        if (!uid || !token || !platform) return;

        // Si usuario quitó permisos manualmente, no insistir
        if (Notification.permission !== 'granted') return;

        const ref = doc(this.db, `users/${uid}/pushTokens/${token}`);
        const snap = await runInInjectionContext(this.injector, () => getDoc(ref));

        // Si no existe o está disabled → rehidratar
        const data = snap.exists() ? (snap.data() as any) : null;
        if (!snap.exists() || data?.enabled === false) {
          console.warn('[WebPush] token missing/disabled → rehidrato', { uid, platform });
          await this.upsertWebToken(uid, platform, token);
        }
      } catch {
        // best-effort
      }
    }, 120_000);
  }

  /**
   * ✅ Marca lastSeenAt (best-effort) con throttle para no spamear Firestore.
   */
  private async markUserSeen(uid: string, reason: string) {
    const now = Date.now();
    if (now - this.lastSeenWriteMs < 60_000) return;
    this.lastSeenWriteMs = now;

    try {
      await setDoc(
        doc(this.db, `users/${uid}`),
        {
          lastSeenAt: serverTimestamp(),
          lastSeenReason: reason,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch {
      // ignore
    }
  }
}
